import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { AuthService } from './auth.service';
import { OrganizationContextService } from './organization-context.service';
import { CustomClaims } from '../data-models/custom-claims.model';
import { GlobalRoles } from '../data-models/global-roles.model';
import { MatchType } from '../data-models/match-type.model';
import { MembershipRoles } from '../data-models/membership-roles.model';

/**
 * Custom Claim Service
 * Provides role-based permission checking for the application
 * Based on: agent-os/specs/01-user-authentication-sso/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class CustomClaimService {
  private organizationContext = inject(OrganizationContextService);

  constructor(private authService: AuthService) {}

  /**
   * Get current user's custom claims
   * Note: user.customClaims is already a CustomClaims object from auth service
   */
  private getClaims(): CustomClaims | null {
    const user = this.authService.getCurrentUser();
    if (!user?.customClaims) {
      return null;
    }

    // user.customClaims is already a CustomClaims instance from auth service
    return user.customClaims;
  }

  /**
   * Check if current user has specific global roles (synchronous)
   */
  hasGlobalClaims(roles: GlobalRoles[], matchType: MatchType = MatchType.Any): boolean {
    const claims = this.getClaims();
    if (!claims) return false;

    switch (matchType) {
      case MatchType.Any:
        return claims.hasAnyGlobalRole(roles);
      case MatchType.All:
        return claims.hasAllGlobalRoles(roles);
      case MatchType.Exact:
        const globalRoles = claims.getGlobalRoles();
        return globalRoles.length === roles.length &&
               roles.every(role => globalRoles.includes(role));
      default:
        return false;
    }
  }

  /**
   * Check if current user has specific membership roles in an organization (synchronous)
   */
  hasMembershipClaims(roles: MembershipRoles[], organizationId: string, matchType: MatchType = MatchType.Any): boolean {
    const claims = this.getClaims();
    if (!claims) return false;

    switch (matchType) {
      case MatchType.Any:
        return claims.hasAnyRoleInOrganization(organizationId, roles);
      case MatchType.All:
        return claims.hasAllRolesInOrganization(organizationId, roles);
      case MatchType.Exact:
        const membership = claims.getMembership(organizationId);
        if (!membership) return false;
        const membershipRoles = membership.getRoles();
        return membershipRoles.length === roles.length &&
               roles.every(role => membershipRoles.includes(role));
      default:
        return false;
    }
  }

  /**
   * Check if current user has specific global role
   */
  hasGlobalRole(role: GlobalRoles): boolean {
    const claims = this.getClaims();
    return claims?.hasGlobalRole(role) ?? false;
  }

  /**
   * Check if current user has specific global role (observable)
   */
  hasGlobalRole$(role: GlobalRoles): Observable<boolean> {
    return this.authService.currentUser$.pipe(
      map(user => {
        if (!user?.customClaims) {
          return false;
        }

        return user.customClaims.hasGlobalRole(role);
      })
    );
  }

  /**
   * Get all global roles for current user
   */
  getGlobalRoles(): GlobalRoles[] {
    const claims = this.getClaims();
    return claims?.getGlobalRoles() ?? [];
  }

  /**
   * Get membership for specific organization
   */
  getMembership(organizationId: string) {
    const claims = this.getClaims();
    return claims?.getMembership(organizationId);
  }

  /**
   * Get all organization IDs user belongs to
   */
  getOrganizationIds(): string[] {
    const claims = this.getClaims();
    return claims?.memberships.map(m => m.organizationId) ?? [];
  }

  /**
   * Check if user is admin (includes both Admin and SuperAdmin)
   */
  isAdmin(): boolean {
    return this.hasGlobalRole(GlobalRoles.Admin) || this.hasGlobalRole(GlobalRoles.SuperAdmin);
  }

  /**
   * Check if user is admin (observable)
   */
  isAdmin$(): Observable<boolean> {
    return this.authService.currentUser$.pipe(
      map(user => {
        if (!user?.customClaims) {
          return false;
        }
        return user.customClaims.hasGlobalRole(GlobalRoles.Admin) || user.customClaims.hasGlobalRole(GlobalRoles.SuperAdmin);
      })
    );
  }

  /**
   * Check if user is entity admin (Owner) in specific organization
   */
  isEntityAdmin(organizationId: string): boolean {
    return this.hasMembershipClaims([MembershipRoles.Owner], organizationId);
  }

  /**
   * Check if user is entity admin (Owner) in specific organization (observable)
   */
  isEntityAdmin$(organizationId: string): Observable<boolean> {
    return this.authService.currentUser$.pipe(
      map(user => {
        if (!user?.customClaims) {
          return false;
        }
        return user.customClaims.hasRoleInOrganization(organizationId, MembershipRoles.Owner);
      })
    );
  }

  /**
   * Check if user has any of the specified membership roles in an organization
   * Uses current organization context if orgId not provided
   *
   * @param roles - Array of MembershipRoles values to check
   * @param orgId - Optional organization ID (uses current org context if not provided)
   * @returns true if user has any of the specified roles
   */
  hasAnyOrgRole(roles: MembershipRoles[], orgId?: string): boolean {
    const organizationId = orgId || this.organizationContext.getCurrentOrganizationId();
    if (!organizationId) {
      return false;
    }

    const claims = this.getClaims();
    if (!claims) {
      return false;
    }

    const membership = claims.getMembership(organizationId);
    if (!membership) {
      return false;
    }

    // Check if any of the specified roles match (bitwise check)
    return roles.some(role => (membership.claims & role) !== 0);
  }

  /**
   * Check if user is Owner or Chief Supervisor in an organization
   * Uses current organization context if orgId not provided
   */
  isOwnerOrChiefSupervisor(orgId?: string): boolean {
    return this.hasAnyOrgRole([MembershipRoles.Owner, MembershipRoles.ChiefSupervisor], orgId);
  }

  /**
   * Check if user is Owner or Chief Supervisor (observable)
   * Uses current organization context if orgId not provided
   */
  isOwnerOrChiefSupervisor$(orgId?: string): Observable<boolean> {
    return this.authService.currentUser$.pipe(
      map(() => this.isOwnerOrChiefSupervisor(orgId))
    );
  }
}
